import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import { cn } from '../../common/functions/cn';
import { useTopLayerPortal } from '../../common/hooks/use-top-layer-portal';
import { preventDialogCloseOnEscape } from '../common-functions';

const DropdownMenu = ({ onOpenChange, ...props }: MenuPrimitive.Root.Props) => {
  return (
    <MenuPrimitive.Root
      data-slot="dropdown-menu"
      onOpenChange={(open, details) => {
        preventDialogCloseOnEscape(open, details);
        onOpenChange?.(open, details);
      }}
      {...props}
    />
  );
};

const DropdownMenuPortal = ({ ...props }: MenuPrimitive.Portal.Props) => {
  return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
};

const DropdownMenuTrigger = ({ ...props }: MenuPrimitive.Trigger.Props) => {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
};

const DropdownMenuContent = ({
  align = 'start',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  className,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<MenuPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'>) => {
  const { portalAnchor, portalProps, positionerProps } = useTopLayerPortal();

  return (
    <>
      {portalAnchor}
      <MenuPrimitive.Portal {...portalProps}>
        <MenuPrimitive.Positioner
          {...positionerProps}
          className="isolate z-50 outline-none"
          align={align}
          alignOffset={alignOffset}
          side={side}
          sideOffset={sideOffset}
        >
          <MenuPrimitive.Popup
            data-slot="dropdown-menu-content"
            className={cn(
              'z-50 max-h-(--available-height) w-(--anchor-width) min-w-32 overflow-y-auto overflow-x-hidden p-1',
              'rounded-md bg-popover text-popover-foreground',
              'shadow-md ring-1 ring-border',
              'origin-(--transform-origin) outline-none duration-100',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2',
              'data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
              'data-open:fade-in-0 data-open:zoom-in-95 data-open:animate-in',
              'data-closed:fade-out-0 data-closed:zoom-out-95 data-closed:animate-out data-closed:overflow-hidden',
              className
            )}
            {...props}
          />
        </MenuPrimitive.Positioner>
      </MenuPrimitive.Portal>
    </>
  );
};

const DropdownMenuGroup = ({ ...props }: MenuPrimitive.Group.Props) => {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;
};

const DropdownMenuLabel = ({
  className,
  inset,
  ...props
}: MenuPrimitive.GroupLabel.Props & {
  inset?: boolean;
}) => {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn('px-1.5 py-1 font-medium text-muted-foreground text-xs data-inset:pl-7', className)}
      {...props}
    />
  );
};

export const dropdownMenuItemBaseStyles = cn(
  'group/dropdown-menu-item relative flex min-h-9 cursor-pointer items-center gap-1.5 px-1.5 py-1 md:min-h-7',
  'text-sm',
  'select-none rounded-sm outline-hidden',
  'data-inset:pl-7',
  'data-disabled:pointer-events-none data-disabled:opacity-50',
  'data-highlighted:bg-accent data-highlighted:text-accent-foreground',
  "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
);

const DropdownMenuItem = ({
  className,
  inset,
  variant = 'default',
  ...props
}: MenuPrimitive.Item.Props & {
  inset?: boolean;
  variant?: 'default' | 'destructive';
}) => {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        dropdownMenuItemBaseStyles,
        'data-[variant=destructive]:text-error data-[variant=destructive]:*:[svg]:text-error',
        'data-[variant=destructive]:data-highlighted:bg-error/10 data-[variant=destructive]:data-highlighted:text-error',
        'not-data-[variant=destructive]:data-highlighted:**:text-primary',
        className
      )}
      {...props}
    />
  );
};

const DropdownMenuLinkItem = ({
  className,
  inset,
  ...props
}: MenuPrimitive.LinkItem.Props & {
  inset?: boolean;
}) => {
  return (
    <MenuPrimitive.LinkItem
      data-slot="dropdown-menu-link-item"
      data-inset={inset}
      className={cn(dropdownMenuItemBaseStyles, className)}
      {...props}
    />
  );
};

const DropdownMenuSub = ({ ...props }: MenuPrimitive.SubmenuRoot.Props) => {
  return <MenuPrimitive.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />;
};

const DropdownMenuSubTrigger = ({
  className,
  inset,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props & {
  inset?: boolean;
}) => {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        'flex min-h-9 cursor-pointer items-center gap-1.5 px-1.5 py-1 md:min-h-7',
        'text-sm',
        'select-none rounded-sm outline-hidden',
        'data-highlighted:bg-accent data-highlighted:text-accent-foreground',
        'not-data-[variant=destructive]:data-highlighted:**:text-primary',
        'data-inset:pl-7',
        'data-popup-open:bg-accent data-popup-open:text-accent-foreground',
        'data-open:bg-accent data-open:text-accent-foreground',
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto" />
    </MenuPrimitive.SubmenuTrigger>
  );
};

const DropdownMenuSubContent = ({
  align = 'start',
  alignOffset = -3,
  side = 'right',
  sideOffset = 0,
  className,
  ...props
}: ComponentProps<typeof DropdownMenuContent>) => {
  return (
    <DropdownMenuContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        'w-auto min-w-[96px] p-1',
        'rounded-md bg-popover text-popover-foreground',
        'shadow-lg ring-1 ring-border',
        'duration-100',
        className
      )}
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      {...props}
    />
  );
};

const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  inset,
  ...props
}: MenuPrimitive.CheckboxItem.Props & {
  inset?: boolean;
}) => {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      data-inset={inset}
      className={cn(
        'relative flex min-h-9 cursor-pointer items-center gap-1.5 py-1 pr-8 pl-1.5 md:min-h-7',
        'text-sm',
        'select-none rounded-sm outline-hidden',
        'data-highlighted:bg-accent data-highlighted:text-accent-foreground data-highlighted:**:text-accent-foreground',
        'data-inset:pl-7',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      checked={checked}
      {...props}
    >
      <span
        className="pointer-events-none absolute right-2 flex items-center justify-center"
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <MenuPrimitive.CheckboxItemIndicator>
          <CheckIcon />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
};

const DropdownMenuRadioGroup = ({ ...props }: MenuPrimitive.RadioGroup.Props) => {
  return <MenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
};

const DropdownMenuRadioItem = ({
  className,
  children,
  inset,
  ...props
}: MenuPrimitive.RadioItem.Props & {
  inset?: boolean;
}) => {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      data-inset={inset}
      className={cn(
        'relative flex min-h-9 cursor-pointer items-center gap-1.5 py-1 pr-8 pl-1.5 md:min-h-7',
        'text-sm',
        'select-none rounded-sm outline-hidden',
        'data-highlighted:bg-accent data-highlighted:text-accent-foreground data-highlighted:**:text-accent-foreground',
        'data-inset:pl-7',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      <span
        className="pointer-events-none absolute right-2 flex items-center justify-center"
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <MenuPrimitive.RadioItemIndicator>
          <CheckIcon />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  );
};

const DropdownMenuSeparator = ({ className, ...props }: MenuPrimitive.Separator.Props) => {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
};

const DropdownMenuShortcut = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        'ml-auto text-muted-foreground text-xs tracking-widest group-data-highlighted/dropdown-menu-item:text-primary',
        className
      )}
      {...props}
    />
  );
};

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuLinkItem,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
